/**
 * 睡眠グラフ（共有コンポーネント）
 *
 * 睡眠ステージ（深い、浅い、REM、覚醒）を時間軸に沿ったタイムラインバーで表示。
 * ホバー時にツールチップをポータル経由で表示。
 * health/sleep-card.tsx と activity-card/sleep-card.tsx の両方から利用。
 */

import { useCallback, useState } from 'react'
import { createPortal } from 'react-dom'

import { formatDurationSeconds, formatTimeJST } from '@/lib/activity-format-utils'
import { cn } from '@/lib/utils'

// 正規化された睡眠レベルデータ
export type SleepLevel = {
  startTime: string // ISO形式
  endTime: string // ISO形式
  level: number // 0=deep, 1=light, 2=rem, 3+=awake
}

// ステージ別の秒数
export type SleepStages = {
  deep: number
  light: number
  rem: number
  awake: number
}

type Props = {
  startTime: string | null
  endTime: string | null
  levels: SleepLevel[]
  stages: SleepStages
}

type HoveredLevel = {
  level: SleepLevel
  x: number
  y: number
} | null

// レベルに応じた色
const getLevelColor = (level: number): string => {
  switch (level) {
    case 0:
      return 'bg-indigo-600' // deep
    case 1:
      return 'bg-blue-400' // light
    case 2:
      return 'bg-purple-500' // REM
    default:
      return 'bg-orange-400' // awake
  }
}

// レベルに応じたラベル
const getLevelLabel = (level: number): string => {
  switch (level) {
    case 0:
      return '深い睡眠'
    case 1:
      return '浅い睡眠'
    case 2:
      return 'REM睡眠'
    default:
      return '覚醒'
  }
}

// 2つの時刻間の分数を計算
const calcDurationMinutes = (startTime: string, endTime: string): number => {
  try {
    return Math.round((new Date(endTime).getTime() - new Date(startTime).getTime()) / 60000)
  } catch {
    return 0
  }
}

// 時間軸ラベル生成（日本時間、2時間間隔）
const generateTimeLabels = (startTime: string | null, endTime: string | null) => {
  if (!startTime || !endTime) return []

  try {
    const start = new Date(startTime)
    const end = new Date(endTime)
    const totalMs = end.getTime() - start.getTime()
    if (totalMs <= 0) return []

    const labels: { time: string; position: number }[] = []
    const fmt = (d: Date) =>
      d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Tokyo' })

    // 開始時刻
    labels.push({ time: fmt(start), position: 0 })

    // 中間ラベル（2時間ごと）
    const intervalMs = 2 * 60 * 60 * 1000
    const startHour = start.getHours()
    const nextEvenHour = Math.ceil((startHour + 1) / 2) * 2
    const labelTime = new Date(start)
    labelTime.setHours(nextEvenHour, 0, 0, 0)

    while (labelTime.getTime() < end.getTime() - intervalMs / 2) {
      const position = ((labelTime.getTime() - start.getTime()) / totalMs) * 100
      if (position > 8 && position < 92) {
        labels.push({ time: fmt(labelTime), position })
      }
      labelTime.setTime(labelTime.getTime() + intervalMs)
    }

    // 終了時刻
    labels.push({ time: fmt(end), position: 100 })

    return labels
  } catch {
    return []
  }
}

const STAGE_CONFIG = [
  { key: 'deep', label: '深い', color: 'bg-indigo-600' },
  { key: 'light', label: '浅い', color: 'bg-blue-400' },
  { key: 'rem', label: 'REM', color: 'bg-purple-500' },
  { key: 'awake', label: '覚醒', color: 'bg-orange-400' },
] as const

export const SleepGraph = ({ startTime, endTime, levels, stages }: Props) => {
  const [hoveredLevel, setHoveredLevel] = useState<HoveredLevel>(null)

  const handleMouseEnter = useCallback((level: SleepLevel, e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect()
    setHoveredLevel({
      level,
      x: rect.left + rect.width / 2,
      y: rect.top,
    })
  }, [])

  const handleMouseLeave = useCallback(() => {
    setHoveredLevel(null)
  }, [])

  const timeLabels = generateTimeLabels(startTime, endTime)
  const totalSeconds = stages.deep + stages.light + stages.rem + stages.awake

  // タイムラインバー生成
  const renderBars = () => {
    if (!startTime || !endTime || levels.length === 0) {
      // フォールバック: 割合表示
      return (
        <div className="flex h-full">
          {STAGE_CONFIG.map((cfg) => {
            const seconds = stages[cfg.key]
            const pct = totalSeconds > 0 ? (seconds / totalSeconds) * 100 : 0
            return <div key={cfg.key} className={cn('transition-all', cfg.color)} style={{ width: `${pct}%` }} />
          })}
        </div>
      )
    }

    const start = new Date(startTime).getTime()
    const totalMs = new Date(endTime).getTime() - start
    if (totalMs <= 0) return null

    return levels.map((level, i) => {
      const startPos = ((new Date(level.startTime).getTime() - start) / totalMs) * 100
      const width = ((new Date(level.endTime).getTime() - new Date(level.startTime).getTime()) / totalMs) * 100

      return (
        <div
          key={i}
          className={cn(
            'absolute h-full cursor-pointer transition-opacity hover:opacity-80',
            getLevelColor(level.level),
          )}
          style={{
            left: `${Math.max(0, startPos)}%`,
            width: `${Math.min(100 - startPos, width)}%`,
          }}
          onMouseEnter={(e) => handleMouseEnter(level, e)}
          onMouseLeave={handleMouseLeave}
        />
      )
    })
  }

  const stageValues = {
    deep: stages.deep,
    light: stages.light,
    rem: stages.rem,
    awake: stages.awake,
  }

  return (
    <div>
      {/* タイムラインバー */}
      <div className="mb-1 relative">
        <div className="relative h-4 rounded-lg overflow-hidden bg-muted/30">{renderBars()}</div>
      </div>

      {/* ホバーツールチップ（ポータル） */}
      {hoveredLevel &&
        createPortal(
          <div
            className="z-9999 bg-popover text-popover-foreground shadow-lg rounded-lg px-3 py-2 border text-xs pointer-events-none whitespace-nowrap"
            style={{
              position: 'fixed',
              left: `${hoveredLevel.x}px`,
              top: `${hoveredLevel.y - 80}px`,
              transform: 'translateX(-50%)',
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <div className={cn('w-2.5 h-2.5 rounded-full', getLevelColor(hoveredLevel.level.level))} />
              <span className="font-medium">{getLevelLabel(hoveredLevel.level.level)}</span>
            </div>
            <div className="text-muted-foreground">
              {formatTimeJST(hoveredLevel.level.startTime)} 〜 {formatTimeJST(hoveredLevel.level.endTime)}
            </div>
            <div className="font-semibold mt-0.5">
              {calcDurationMinutes(hoveredLevel.level.startTime, hoveredLevel.level.endTime)}分間
            </div>
          </div>,
          document.body,
        )}

      {/* 時間軸 */}
      <div className="relative h-5 mb-4">
        {timeLabels.map((label, i) => (
          <div
            key={i}
            className="absolute flex flex-col items-center"
            style={{
              left: `${label.position}%`,
              transform:
                i === 0 ? 'translateX(0)' : i === timeLabels.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)',
            }}
          >
            <div className="w-px h-1.5 bg-muted-foreground/30" />
            <span className="text-[10px] text-muted-foreground tabular-nums">{label.time}</span>
          </div>
        ))}
      </div>

      {/* ステージ凡例 */}
      <div className="grid grid-cols-4 gap-1 text-xs">
        {STAGE_CONFIG.map((cfg) => (
          <div key={cfg.key} className="flex flex-col items-center p-1">
            <div className="flex gap-2 items-center mb-1">
              <div className={cn('w-3 h-3 rounded-full', cfg.color)} />
              <span className="text-muted-foreground">{cfg.label}</span>
            </div>
            <span className="font-medium tabular-nums">{formatDurationSeconds(stageValues[cfg.key])}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
